/**
 * Tool Info Page — "read about this tool" detail page.
 * Single component reused for cost-calculator, deal-analyzer, negotiation-coach.
 * Bundle is the only purchase path → all CTAs go to /tools-bundle.
 */

import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  ArrowLeft, Calculator, FileSearch, MessageSquareText, Check, Sparkles,
  ChevronRight, ShieldCheck, DollarSign, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';

const TOOLS = {
  'cost-calculator': {
    icon: Calculator,
    image: '/tool-images/cost-calculator.png',
    badge: '5-year ownership cost',
    title: 'RV Cost Calculator',
    tagline: 'See the TRUE cost of owning an RV — beyond the sticker price.',
    color: 'emerald',
    accent: 'from-emerald-500 to-emerald-600',
    iconBg: 'bg-emerald-500/15 text-emerald-500',
    individualPrice: '$199',
    description: [
      'The dealership only shows you the monthly payment. The Cost Calculator shows you everything else: depreciation, fuel, insurance, storage, maintenance, campground fees, registration, and financing interest.',
      'Get a year-by-year breakdown of what your RV will actually cost you over 5 years — so you know what you can really afford before you sign.',
    ],
    features: [
      { icon: DollarSign, text: 'Total 5-year cost of ownership' },
      { icon: BarChart3,  text: 'Year-by-year cost breakdown chart' },
      { icon: ShieldCheck, text: 'Insurance & financing estimates included' },
      { icon: Sparkles,   text: 'Compare RV models side-by-side' },
    ],
    sample: {
      label: 'Sample Calculation',
      lines: [
        ['Sticker price', '$45,000'],
        ['Financing interest (60mo @ 7.5%)', '+ $9,200'],
        ['Insurance & registration', '+ $4,800'],
        ['Fuel + campground fees', '+ $7,100'],
        ['Maintenance + storage', '+ $5,400'],
        ['TRUE 5-year cost', '$71,500'],
      ],
      footnote: 'Avg monthly: $1,192 — that\'s 60% more than the loan payment alone.',
    },
  },
  'deal-analyzer': {
    icon: FileSearch,
    image: '/tool-images/deal-analyzer.png',
    badge: 'AI deal grader',
    title: 'RV Deal Analyzer',
    tagline: 'Stop guessing. Know in 30 seconds whether the price is fair, a steal, or a rip-off.',
    color: 'sky',
    accent: 'from-sky-500 to-sky-600',
    iconBg: 'bg-sky-500/15 text-sky-500',
    individualPrice: '$199',
    description: [
      'Paste a listing or upload a photo of the dealer\'s offer sheet. The AI compares it to thousands of similar units across the country and tells you whether you\'re overpaying — and by how much.',
      'You also get a ready-to-send counter-offer with the math behind it, so you walk into the dealership knowing your number.',
    ],
    features: [
      { icon: Sparkles,    text: 'AI compares to comparable national listings' },
      { icon: BarChart3,   text: 'Steal / Fair / Overpriced verdict with reasons' },
      { icon: DollarSign,  text: 'Counter-offer suggestion + walk-away price' },
      { icon: ShieldCheck, text: 'Catches hidden fees & dealer add-on traps' },
    ],
    sample: {
      label: 'Sample Verdict',
      lines: [
        ['Listed price', '$58,995'],
        ['Market average (similar unit)', '$51,200'],
        ['Verdict', 'OVERPRICED'],
        ['Suggested counter-offer', '$49,800'],
        ['Walk-away price', '$53,500'],
      ],
      footnote: 'You could save up to $9,195 by negotiating with these numbers.',
    },
  },
  'negotiation-coach': {
    icon: MessageSquareText,
    image: '/tool-images/negotiation-coach.png',
    badge: 'Word-for-word scripts',
    title: 'RV Negotiation Coach',
    tagline: 'The exact words to say to lock in a fair deal — even if you\'ve never negotiated before.',
    color: 'rose',
    accent: 'from-rose-500 to-rose-600',
    iconBg: 'bg-rose-500/15 text-rose-500',
    individualPrice: '$199',
    description: [
      'Most buyers lose thousands at the dealership because they don\'t know what to say. The Negotiation Coach gives you the opening line, the counter-offer math, the comebacks for every dealer pushback, and a clear walk-away threshold.',
      'It\'s like having a former RV sales manager whispering in your ear during the deal.',
    ],
    features: [
      { icon: MessageSquareText, text: 'Word-for-word opening line for the dealer' },
      { icon: BarChart3,         text: 'Counter-offer math & justification' },
      { icon: Sparkles,          text: 'Comebacks for the 8 most common pushbacks' },
      { icon: ShieldCheck,       text: 'Walk-away signals so you never overpay' },
    ],
    sample: {
      label: 'Sample Opening Line',
      lines: [
        ['Your line', '"I\'ve seen this exact unit at $51,200 — what can we do today?"'],
        ['If they say "we can\'t go that low"', '"What can you do? I\'m ready to sign right now at $52,500 out the door."'],
        ['If they push warranty/add-ons', '"Show me the line items. I\'ll consider service contract, but skip everything else."'],
      ],
      footnote: 'Buyers who use this script save an average of $4,300 on their deal.',
    },
  },
};

const ToolInfoPage = () => {
  const { toolSlug } = useParams();
  const { isDark } = useTheme();
  const tool = TOOLS[toolSlug];

  if (!tool) {
    return <Navigate to="/" replace />;
  }

  const Icon = tool.icon;

  // Tool squeeze pages always render in the new cinematic dark theme so
  // they match the homepage after a user clicks through from the tools
  // section. This intentionally ignores the global isDark toggle.
  return (
    <div className="min-h-screen bg-[#05070d] text-white" data-testid={`tool-info-${toolSlug}`}>
      {/* Top nav */}
      <div className="border-b border-white/10 bg-[#05070d]/85 sticky top-0 z-30 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white" data-testid="back-home">
            <ArrowLeft size={16} /> Back to home
          </Link>
          <Link
            to="/tools-bundle"
            className="inline-flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-full bg-gradient-to-r from-cyan-300 via-sky-400 to-indigo-400 text-[#05070d] font-bold text-xs sm:text-sm shadow-[0_0_20px_-6px_rgba(56,189,248,0.6)] hover:shadow-[0_0_30px_-2px_rgba(56,189,248,0.85)] transition-shadow whitespace-nowrap"
            data-testid="header-bundle-cta"
          >
            <span className="hidden sm:inline">Get all 3 for $97</span>
            <span className="sm:hidden">$97 Bundle</span>
            <ChevronRight size={14} />
          </Link>
        </div>
      </div>

      {/* Background atmosphere */}
      <div aria-hidden="true" className="pointer-events-none fixed inset-0 -z-0">
        <div className="absolute -top-40 -left-32 w-[36rem] h-[36rem] rounded-full bg-cyan-500 opacity-15 blur-[120px]" />
        <div className="absolute bottom-0 -right-32 w-[36rem] h-[36rem] rounded-full bg-indigo-500 opacity-15 blur-[120px]" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        {/* Hero */}
        <div className="text-center mb-10 md:mb-14">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${tool.iconBg} mb-5`}>
            <Icon className="w-8 h-8" />
          </div>
          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4 bg-white/10 text-cyan-200">
            {tool.badge}
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4 text-white">
            {tool.title}
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto text-slate-300">
            {tool.tagline}
          </p>
        </div>

        {/* Product hero image */}
        {tool.image && (
          <div className="mb-10 md:mb-14 rounded-3xl overflow-hidden ring-1 ring-white/10 bg-white/[0.03] shadow-2xl">
            <img
              src={tool.image}
              alt={`${tool.title} product`}
              className="w-full aspect-[16/10] object-cover"
              loading="lazy"
              data-testid={`tool-hero-image-${toolSlug}`}
            />
          </div>
        )}

        {/* Description */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 md:p-8 mb-6 space-y-4">
          {tool.description.map((p, i) => (
            <p key={i} className="text-base leading-relaxed text-slate-300">{p}</p>
          ))}
        </div>

        {/* Features */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 md:p-8 mb-6">
          <h2 className="text-lg font-bold mb-5 text-white">What you get</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tool.features.map((f, i) => {
              const FIcon = f.icon;
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className={`shrink-0 w-9 h-9 rounded-lg ${tool.iconBg} flex items-center justify-center`}>
                    <FIcon className="w-4 h-4" />
                  </div>
                  <p className="pt-1.5 text-sm text-slate-200">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sample */}
        <div className="rounded-2xl border-2 border-dashed border-white/10 bg-white/[0.025] p-6 md:p-8 mb-10">
          <p className="text-xs font-semibold uppercase tracking-wide mb-4 text-cyan-300">{tool.sample.label}</p>
          <div className="space-y-2.5">
            {tool.sample.lines.map(([k, v], i) => {
              const isLast = i === tool.sample.lines.length - 1;
              return (
                <div
                  key={i}
                  className={`flex items-start justify-between gap-4 ${isLast ? 'pt-3 mt-1 border-t border-white/10' : ''}`}
                >
                  <span className="text-sm text-slate-400">{k}</span>
                  <span className={`text-sm font-semibold text-right ${isLast ? 'text-white text-base' : 'text-slate-200'}`}>{v}</span>
                </div>
              );
            })}
          </div>
          {tool.sample.footnote && (
            <p className="mt-4 text-xs italic text-slate-400">{tool.sample.footnote}</p>
          )}
        </div>

        {/* Individual price + bundle savings callout */}
        <div className="rounded-2xl border-2 border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 md:p-7 mb-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest mb-2 text-cyan-300">
            Individual price for this tool
          </p>
          <p className="text-3xl md:text-4xl font-extrabold mb-1 text-white">
            {tool.individualPrice}
          </p>
          <p className="text-sm text-slate-400">
            Or get <span className="font-bold text-cyan-300">all 3 tools</span> in the bundle for just <span className="font-bold text-white">$97</span> — that's <span className="line-through opacity-70">$597</span> of value, you save <span className="font-bold text-emerald-400">$500</span>.
          </p>
        </div>

        {/* Bundle CTA — $97 standard bundle */}
        <div className="rounded-3xl bg-gradient-to-br from-cyan-300 via-sky-400 to-indigo-400 p-8 md:p-10 text-center text-[#05070d] shadow-[0_0_60px_-12px_rgba(56,189,248,0.6)]">
          <p className="text-sm font-bold uppercase tracking-wider mb-2 opacity-80">Best deal — bundle pricing</p>
          <h3 className="text-2xl md:text-3xl font-extrabold mb-2">Bundle all 3 RV tools — $97</h3>
          <p className="text-base mb-1 opacity-90">
            Cost Calculator + Deal Analyzer + Negotiation Coach. Lifetime access. One payment.
          </p>
          <p className="text-sm mb-6 opacity-80">
            Each tool is <span className="font-bold">$199</span> separately (<span className="line-through">$597</span> total) — you save <span className="font-bold">$500</span>.
          </p>
          <Link to="/tools-bundle">
            <Button
              className="bg-[#05070d] hover:bg-[#0c1224] text-white font-bold px-8 py-6 rounded-full text-base"
              data-testid={`bundle-cta-${toolSlug}`}
            >
              <Check className="w-5 h-5 mr-2" /> Get Lifetime Access — $97
            </Button>
          </Link>
        </div>

        {/* Other tools */}
        <div className="mt-10">
          <p className="text-center text-sm font-semibold uppercase tracking-wide mb-4 text-slate-400">The other 2 tools in your bundle</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(TOOLS).filter(([k]) => k !== toolSlug).map(([k, t]) => {
              const TIcon = t.icon;
              return (
                <Link
                  key={k}
                  to={`/tool-info/${k}`}
                  className="flex items-center gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] hover:border-white/20 transition-colors"
                  data-testid={`other-tool-${k}`}
                >
                  <div className={`shrink-0 w-10 h-10 rounded-lg ${t.iconBg} flex items-center justify-center`}>
                    <TIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-white">{t.title}</p>
                    <p className="text-xs truncate text-slate-400">{t.tagline}</p>
                  </div>
                  <ChevronRight className="shrink-0 w-4 h-4 text-slate-500" />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolInfoPage;
